/**
 * 折线图横轴展示：日期 MM/DD、小时 HH:mm、刻度抽稀（与 shell2-app BusinessLineChart 对齐）。
 */

/** 横轴默认最多显示的刻度个数（含起点，末位可不落在最后一天；容器宽度未知时的回退） */
export const DEFAULT_CURVE_AXIS_MAX_TICKS = 7;
/** 估算单个横轴标签（12px MM/DD）所需最小水平像素 */
export const CURVE_AXIS_TICK_MIN_WIDTH_PX = 52;
/** 绘图区左侧预留给 Y 轴刻度的宽度（像素） */
export const CURVE_AXIS_Y_AXIS_RESERVE_PX = 56;
/** 按屏宽自动抽稀时横轴至少显示的刻度数 */
export const CURVE_AXIS_MIN_TICKS = 4;

/**
 * 将后端曲线横轴 `x`（多为 YYYY-MM-DD）格式化为短标签，用于 X 轴刻度。
 * @param x - 接口返回的日期字符串
 * @returns `MM/DD`；无法解析时原样返回
 */
export function formatCurveAxisDateLabel(x: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(x.trim());
    if (m) return `${m[2]}/${m[3]}`;
    return x;
}

/**
 * 将小时粒度横轴 `x` 格式化为 `HH:mm`（如 `00:00`、`09:00`）。
 * @param x - 接口返回的时间字符串
 * @returns `HH:mm`；无法解析时原样返回
 */
export function formatCurveAxisHourLabel(x: string): string {
    const trimmed = x.trim();
    const hmOnly = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
    if (hmOnly) {
        const hour = Number(hmOnly[1]);
        const minute = Number(hmOnly[2]);
        if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
            return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
    }
    const dt = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})/.exec(trimmed);
    if (dt) {
        const hour = Number(dt[4]);
        const minute = Number(dt[5]);
        if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
            return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
    }
    if (/^\d{1,2}$/.test(trimmed)) {
        const hour = Number(trimmed);
        if (hour >= 0 && hour <= 23) {
            return `${String(hour).padStart(2, '0')}:00`;
        }
    }
    return trimmed;
}

/**
 * 按 `granularity` 选择横轴短标签：小时为 `HH:mm`，否则为 `MM/DD`。
 * @param x - series 单点横轴值
 * @param granularity - 如 `hour`、`day`
 */
export function formatCurveAxisLabel(x: string, granularity?: string): string {
    if (granularity === 'hour') {
        return formatCurveAxisHourLabel(x);
    }
    return formatCurveAxisDateLabel(x);
}

/**
 * 从起点起按固定索引步长生成横轴刻度索引（等间隔、可不包含末日）。
 * @param dataLength - 数据点个数
 * @param maxTickCount - 最多显示刻度数
 */
export function buildEqualSpacedXAxisTickIndices(dataLength: number, maxTickCount: number): number[] {
    if (dataLength <= 0) return [];
    if (dataLength === 1) return [0];
    const cap = Math.min(Math.max(1, maxTickCount), dataLength);
    if (cap === 1) return [0];
    const lastIndex = dataLength - 1;
    const indexStep = Math.max(1, Math.ceil(lastIndex / cap));
    const indices: number[] = [0];
    let i = indexStep;
    while (i < lastIndex && indices.length < cap) {
        indices.push(i);
        i += indexStep;
    }
    return indices;
}

/**
 * 根据容器宽度估算横轴可显示的刻度上限（与 App {@link BusinessLineChart} 逻辑对齐）。
 * @param dataLength - 数据点个数
 * @param containerWidthPx - 折线图容器宽度（含 Y 轴区域）
 * @param explicitMaxTicks - 调用方指定上限时优先使用
 */
export function resolveCurveAxisMaxTicks(
    dataLength: number,
    containerWidthPx: number,
    explicitMaxTicks?: number,
): number {
    if (dataLength <= 0) {
        return 0;
    }
    if (explicitMaxTicks != null && explicitMaxTicks > 0) {
        return Math.min(dataLength, Math.max(CURVE_AXIS_MIN_TICKS, Math.round(explicitMaxTicks)));
    }
    const plotWidth = Math.max(0, containerWidthPx - CURVE_AXIS_Y_AXIS_RESERVE_PX);
    if (plotWidth <= 0) {
        return Math.min(dataLength, DEFAULT_CURVE_AXIS_MAX_TICKS);
    }
    const byWidth = Math.floor(plotWidth / CURVE_AXIS_TICK_MIN_WIDTH_PX);
    const capped = Math.max(CURVE_AXIS_MIN_TICKS, byWidth);
    return Math.min(dataLength, capped);
}

/** 抽稀横轴配置（Apex categories 与刻度索引） */
export interface SparseCurveAxisConfig {
    /** 与数据点等长的短标签（MM/DD 或 HH:mm） */
    categories: string[];
    /** 应显示刻度的数据点下标 */
    tickIndices: number[];
}

/**
 * 由原始横轴 `x` 列表生成 Apex `categories` 与抽稀下标。
 * @param params.rawLabels - 接口原始横轴值（tooltip 用）
 * @param params.granularity - 时间聚合粒度
 * @param params.maxTicks - 最多刻度数，默认 {@link DEFAULT_CURVE_AXIS_MAX_TICKS}
 */
export function buildSparseCurveAxisConfig(params: {
    rawLabels: string[];
    granularity?: string;
    maxTicks?: number;
}): SparseCurveAxisConfig {
    const { rawLabels, granularity, maxTicks = DEFAULT_CURVE_AXIS_MAX_TICKS } = params;
    const categories = rawLabels.map((x) => formatCurveAxisLabel(x, granularity));
    const tickIndices = buildEqualSpacedXAxisTickIndices(rawLabels.length, maxTicks);
    return { categories, tickIndices };
}

/**
 * Apex `xaxis.labels.formatter`：仅在抽稀下标处显示短标签（备用；推荐 {@link buildSparseCurveAxisCategoriesForApex}）。
 * Apex 部分场景第二参为 tick 下标而非 opts.dataPointIndex。
 */
export function createSparseXAxisLabelsFormatter(
    categories: string[],
    tickIndices: number[],
): (value: string, tickIndex: number, opts?: { dataPointIndex?: number }) => string {
    const tickSet = new Set(tickIndices);
    return (value, tickIndex, opts) => {
        const i =
            typeof opts?.dataPointIndex === 'number'
                ? opts.dataPointIndex
                : typeof tickIndex === 'number' && tickIndex >= 0 && tickIndex < categories.length
                  ? tickIndex
                  : categories.indexOf(value);
        if (i >= 0 && tickSet.has(i)) {
            return categories[i] ?? value;
        }
        return '';
    };
}

/**
 * 由原始横轴 `x` 列表生成 Apex `categories`（非刻度位置填空串，避免 formatter 拿不到下标导致整轴不显示）。
 * @param params.rawLabels - 接口原始横轴值（tooltip 用完整值）
 */
export function buildSparseCurveAxisCategoriesForApex(params: {
    rawLabels: string[];
    granularity?: string;
    maxTicks?: number;
}): string[] {
    const sparse = buildSparseCurveAxisConfig(params);
    const tickSet = new Set(sparse.tickIndices);
    return sparse.categories.map((label, i) => (tickSet.has(i) ? label : ''));
}

/**
 * Apex `tooltip.x.formatter`：悬停展示完整原始横轴值。
 * @param rawLabels - 与数据点等长的原始 x
 */
export function createCurveAxisTooltipXFormatter(
    rawLabels: string[],
): (val: number, opts?: { dataPointIndex?: number; w?: { globals?: { labels?: string[] } } }) => string {
    return (_val, opts) => {
        const i = opts?.dataPointIndex;
        if (typeof i === 'number' && i >= 0 && i < rawLabels.length) {
            return rawLabels[i];
        }
        /** 部分 Apex 版本 tooltip 仅传类目下标 val */
        const fromGlobals = opts?.w?.globals?.labels?.[typeof _val === 'number' ? _val - 1 : -1];
        if (fromGlobals) {
            return fromGlobals;
        }
        return '';
    };
}
